import { z } from 'zod';
import { RowLabelOptionalSchema } from './row-label';

// ============================================================
// Insect Pressure — stored as JSONB on the observation
// (pest_disease_observations_company.insect_pressure). One entry per crop type
// + surveillance pest (admin → Surveillance) given a level — an observation can
// cover blocks of several crops, and one pest can apply to more than one of
// them. The pest name is a snapshot so a later rename in admin doesn't change a
// saved observation. crop_type is optional only so entries saved before it
// existed still read.
// ============================================================

export const INSECT_PRESSURE_LEVELS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

export const InsectPressureLevelSchema = z.enum(['high', 'medium', 'low']);

export const PestDiseaseInsectPressureSchema = z
  .array(
    z.object({
      crop_type: z.string().max(200).optional(),
      pest_id: z.number().int(),
      pest: z.string().max(150),
      level: InsectPressureLevelSchema,
    }),
  )
  .max(200);

// Recommendations typed under each Insect Pressure card (one per crop type).
// Stored as JSONB in pest_disease_observations_company.insect_pressure_recommendations.
export const PestDiseaseInsectPressureRecommendationsSchema = z
  .array(
    z.object({
      crop_type: z.string().max(200),
      recommendations: z.string().max(5000),
    }),
  )
  .max(100);

// ============================================================
// Image metadata
// ============================================================

export const PestDiseaseImageMetadataSchema = z.object({
  id: z.number().int().positive(),
  observation_id: z.number().int(),
  company_id: z.string().uuid().nullable().optional(),
  image_path: z.string(),
  image_url: z.string().nullable().optional(), // resolved signed URL (read only)
  note: z.string().nullable().optional(),
  planting_reference: z.string().max(255).nullable().optional(),
  display_order: z.number().int().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  deleted_at: z.string().nullable().optional(),
});

export const PestDiseaseImageMetadataCreateSchema = z.object({
  image_path: z.string(),
  note: z.string().nullable().optional(),
  planting_reference: z.string().max(255).nullable().optional(),
  display_order: z.number().int().nullable().optional(),
});

export const PestDiseaseImageMetadataUpdateSchema = z.object({
  note: z.string().nullable().optional(),
  planting_reference: z.string().max(255).nullable().optional(),
  display_order: z.number().int().nullable().optional(),
});

// ============================================================
// Notes
// ============================================================

export const PestDiseaseNoteSchema = z.object({
  id: z.number().int().positive(),
  pest_observation_id: z.number().int(),
  company_id: z.string().uuid().nullable().optional(),
  note: z.string(),
  planting_reference: z.string().max(255).nullable().optional(),
  created_by: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export const PestDiseaseNoteCreateSchema = z.object({
  note: z.string().min(1),
  planting_reference: z.string().max(255).nullable().optional(),
});

// ============================================================
// Plantings (per-observation breakdown)
// ============================================================

export const PestDiseasePlantingSchema = z.object({
  id: z.number().int().positive(),
  observation_id: z.number().int(),
  company_id: z.string().uuid().nullable().optional(),
  ref_id: z.number().int().nullable().optional(),
  reference: z.string().max(100),
  year: z.string().max(100).nullable().optional(),
  week: z.string().max(100).nullable().optional(),
  start_row: z.string().max(100).nullable().optional(),
  finish_row: z.string().max(100).nullable().optional(),
  crop: z.string().max(120).nullable().optional(),
  crop_variety: z.string().max(200).nullable().optional(),
  percentage_area: z.number().nullable().optional(),
  total_hectares: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export const PestDiseasePlantingInputSchema = z.object({
  ref_id: z.number().int().nullable().optional(),
  reference: z.string().max(100),
  year: z.string().max(100).nullable().optional(),
  week: z.union([z.string(), z.number()]).nullable().optional(),
  start_row: z.union([z.string(), z.number()]).nullable().optional(),
  finish_row: z.union([z.string(), z.number()]).nullable().optional(),
  crop: z.string().max(120).nullable().optional(),
  crop_variety: z.string().max(200).nullable().optional(),
  percentage_area: z.coerce.number().nullable().optional(),
  total_hectares: z.coerce.number().nullable().optional(),
});

// ============================================================
// Emails sent log
// ============================================================

export const PestDiseaseEmailSentSchema = z.object({
  id: z.number().int().positive(),
  pest_observation_id: z.number().int().nullable().optional(),
  company_id: z.string().uuid().nullable().optional(),
  subject: z.string().max(250).nullable().optional(),
  message: z.string().nullable().optional(),
  recipients: z.string().nullable().optional(),
  attachment_pdf_url: z.string().nullable().optional(),
  created_by: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
});

export const PestDiseaseEmailSendSchema = z.object({
  // Both optional: a blank subject gets "Pest & Disease Observation #id — block · reference"
  // from the API, and the email template simply leaves out an empty message.
  subject: z.string().trim().max(250).optional().default(''),
  message: z.string().max(10000).optional().default(''),
  recipients: z.array(z.string().email()).min(1),
  // Typed-in addresses copied on the email (not from staff management).
  cc: z.array(z.string().email()).max(50).optional().default([]),
  attach_pdf: z.boolean().optional().default(false),
  pdf_data_url: z.string().nullable().optional(), // base64 PDF generated client-side
});

// ============================================================
// Pest observation spray task link (legacy table; now also stored on
// spray_template via pest_observation_id FK)
// ============================================================

export const PestObservationSprayTaskSchema = z.object({
  id: z.number().int().positive(),
  pest_observation_id: z.number().int(),
  company_id: z.string().uuid(),
  task_type: z.string().max(100).nullable().optional(),
  tank_size: z.string().max(50).nullable().optional(),
  water_rate: z.string().max(50).nullable().optional(),
  total_hectares: z.number().nullable().optional(),
  wind_speed_saved: z.string().max(50).nullable().optional(),
  spray_pressure: z.string().max(50).nullable().optional(),
  spray_color: z.string().max(50).nullable().optional(),
  spray_jet_size: z.string().max(50).nullable().optional(),
  vehicle_used: z.string().max(100).nullable().optional(),
  tempreature: z.string().max(50).nullable().optional(),
  humidity: z.string().max(50).nullable().optional(),
  windspeed: z.string().max(50).nullable().optional(),
  direction: z.string().max(50).nullable().optional(),
  chemical_ids: z.string().nullable().optional(),
  saved_chemical: z.string().max(255).nullable().optional(),
  fertilizer_ids: z.string().nullable().optional(),
  total_cost_products: z.number().nullable().optional(),
  start_time: z.string().nullable().optional(),
  finish_time: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export const PestObservationSprayTaskInputSchema = PestObservationSprayTaskSchema.omit({
  id: true,
  pest_observation_id: true,
  company_id: true,
  created_at: true,
  updated_at: true,
}).partial();

// ============================================================
// Observation
// ============================================================

export const PestDiseaseObservationSchema = z.object({
  id: z.number().int().positive(),
  company_id: z.string().uuid(),
  company_name: z.string().max(350).nullable().optional(),
  username: z.string().max(350).nullable().optional(),
  year: z.string().max(4).nullable().optional(),
  week: z.number().int().nullable().optional(),
  week_number: z.string().nullable().optional(),
  crop_number: z.number().int().nullable().optional(),
  block_name: z.string().max(300).nullable().optional(),
  block_type: z.string().max(80).nullable().optional(),
  block_id: z.number().int().nullable().optional(),
  reference: z.string().max(100).nullable().optional(),
  crop: z.string().max(120).nullable().optional(),
  cropType: z.string().max(200).nullable().optional(),
  crop_variety: z.string().max(150).nullable().optional(),
  task: z.string().max(255).nullable().optional(),
  task_status: z.string().max(100).nullable().optional(),
  notes: z.string().nullable().optional(),
  photo_url: z.string().nullable().optional(),
  image_data: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  finish_time: z.string().nullable().optional(),
  saved_timer_time: z.number().int().nullable().optional(),
  is_manual_entry: z.number().int().nullable().optional(),
  start_row: RowLabelOptionalSchema,
  finish_row: RowLabelOptionalSchema,
  labour_id: z.string().nullable().optional(),
  seed_supplier: z.string().max(255).nullable().optional(),
  plants_per_hectare: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  created_by: z.string().nullable().optional(),
  updated_by: z.string().nullable().optional(),
});

export const PestDiseaseObservationCreateSchema = PestDiseaseObservationSchema.omit({
  id: true,
  company_id: true,
  created_at: true,
  updated_at: true,
}).extend({
  section_id: z.number().int().nullable().optional(),
  count: z.number().int().nullable().optional(),
  pack_category: z.string().max(150).nullable().optional(),
  pack_type: z.string().max(150).nullable().optional(),
  vehicle: z.string().max(255).nullable().optional(),
  insect_pressure: PestDiseaseInsectPressureSchema.nullable().optional(),
  insect_pressure_recommendations: PestDiseaseInsectPressureRecommendationsSchema.nullable().optional(),
  // Optional nested writes — when present, the API replaces the related rows.
  plantings: z.array(PestDiseasePlantingInputSchema).nullable().optional(),
  images: z.array(PestDiseaseImageMetadataCreateSchema).nullable().optional(),
  spray_task: PestObservationSprayTaskInputSchema.nullable().optional(),
});

export const PestDiseaseObservationUpdateSchema = PestDiseaseObservationCreateSchema.partial();

export const PestDiseaseObservationListQuerySchema = z.object({
  search: z.string().optional(),
  block_type: z.string().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  page: z.coerce.number().int().positive().optional().default(1),
  per_page: z.coerce.number().int().positive().max(100).optional().default(25),
});

export const PestDiseaseObservationDetailQuerySchema = z.object({
  include: z.string().optional(),
});

// ============================================================
// Photo upload (single image to bucket)
// ============================================================

export const PestDiseasePhotoUploadResponseSchema = z.object({
  image_path: z.string(),
  image_url: z.string().nullable().optional(),
});

// ============================================================
// Types
// ============================================================

export type PestDiseaseObservation = z.infer<typeof PestDiseaseObservationSchema>;
export type PestDiseaseObservationCreate = z.infer<typeof PestDiseaseObservationCreateSchema>;
export type PestDiseaseObservationUpdate = z.infer<typeof PestDiseaseObservationUpdateSchema>;
export type PestDiseaseObservationListQuery = z.infer<typeof PestDiseaseObservationListQuerySchema>;
export type PestDiseaseObservationDetailQuery = z.infer<typeof PestDiseaseObservationDetailQuerySchema>;

export type PestDiseaseImageMetadata = z.infer<typeof PestDiseaseImageMetadataSchema>;
export type PestDiseaseImageMetadataCreate = z.infer<typeof PestDiseaseImageMetadataCreateSchema>;
export type PestDiseaseImageMetadataUpdate = z.infer<typeof PestDiseaseImageMetadataUpdateSchema>;

export type PestDiseaseNote = z.infer<typeof PestDiseaseNoteSchema>;
export type PestDiseaseNoteCreate = z.infer<typeof PestDiseaseNoteCreateSchema>;

export type PestDiseasePlanting = z.infer<typeof PestDiseasePlantingSchema>;
export type PestDiseasePlantingInput = z.infer<typeof PestDiseasePlantingInputSchema>;

export type PestDiseaseEmailSent = z.infer<typeof PestDiseaseEmailSentSchema>;
export type PestDiseaseEmailSend = z.infer<typeof PestDiseaseEmailSendSchema>;

export type PestObservationSprayTask = z.infer<typeof PestObservationSprayTaskSchema>;
export type PestObservationSprayTaskInput = z.infer<typeof PestObservationSprayTaskInputSchema>;

export type PestDiseasePhotoUploadResponse = z.infer<typeof PestDiseasePhotoUploadResponseSchema>;

export type InsectPressureLevel = z.infer<typeof InsectPressureLevelSchema>;
export type PestDiseaseInsectPressure = z.infer<typeof PestDiseaseInsectPressureSchema>;
export type PestDiseaseInsectPressureRecommendations = z.infer<typeof PestDiseaseInsectPressureRecommendationsSchema>;
